/**
 * TruthReconcileCard — the "D1 ⟷ Shopify (orders/inventory)" result block
 * (#1576, unified into the single reconcile action #1584-followup). Since
 * #2484 it is the Shopify side row of the delivered pane's reconcile card.
 *
 * A read-only presentational block that sits beside the Onfleet-side
 * `ReconcileCheckCard`. Both are driven by the ONE "Reconcile" action in the
 * dispatch header: the parent (`DispatchDashboard`) owns the
 * `GET /api/dispatch/truth-reconcile` query (so the shared action can fan out
 * to both truth sources at once and drive a single combined loading state)
 * and feeds this component the `triggered` / `report` / `isFetching` /
 * `isError` / `error` view state. Each `ReconcileItem` renders as a
 * D1 ⟷ Shopify pair with a verdict badge, the reproducible `method` recipe,
 * expandable `evidence`, and `action` advice.
 *
 * Layout (#1676): this renders as ONE ROW of the dispatch checklist — same
 * shape as "尚未收款" and the rest — collapsed unless it has drift to show.
 * Severity-first inside (#1583-follow-up): the summary is a row of colored
 * verdict TILES that double as filters. Rows are ordered drift → maybe_skew
 * → unknown, and matches are collapsed out of the way by default (revealed by
 * clicking the match tile) so the real problems are never buried under a pile
 * of green matches. The single global as-of stamp lives on the summary block;
 * the repeated per-side row timestamps were dropped as noise. Shopify is the
 * authority; this panel only reports (no compensating writes — read-only).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeProps } from '@resource-kit/ui/react';
import { CollapsibleCheck, ReconcileBadge } from './CollapsibleCheck';
import type { components } from '@/types/api.generated';
import { getMerchantTz } from '@lib/merchant-tz';

type TruthReconcileReport = components['schemas']['DispatchTruthReconcileReport'];
type ReconcileItem = TruthReconcileReport['items'][number];
type Verdict = ReconcileItem['verdict'];

const PARSE_FAILURE_KIND_LABEL = {
  malformed_json: 'dispatch.salesBridge.parseFailure.kind.malformed_json',
  schema_invalid: 'dispatch.salesBridge.parseFailure.kind.schema_invalid',
  invalid_created_at: 'dispatch.salesBridge.parseFailure.kind.invalid_created_at',
  missing_order: 'dispatch.salesBridge.parseFailure.kind.missing_order',
  unknown: 'dispatch.salesBridge.parseFailure.kind.unknown',
} as const;

function parseFailureKindLabel(kind: string): string {
  if (Object.prototype.hasOwnProperty.call(PARSE_FAILURE_KIND_LABEL, kind)) {
    return PARSE_FAILURE_KIND_LABEL[kind as keyof typeof PARSE_FAILURE_KIND_LABEL];
  }
  return PARSE_FAILURE_KIND_LABEL.unknown;
}

// Verdict → badge appearance + i18n label. The closed-set map derives the
// `Verdict` union at compile time (`satisfies Record<Verdict, …>`), so a new
// backend verdict fails the build here instead of silently rendering an
// unstyled badge — no magic verdict strings scattered across the component.
const VERDICT_BADGE = {
  match: { variant: 'success', labelKey: 'dispatch.truthReconcile.verdict.match' },
  drift: { variant: 'destructive', labelKey: 'dispatch.truthReconcile.verdict.drift' },
  maybe_skew: { variant: 'secondary', labelKey: 'dispatch.truthReconcile.verdict.maybe_skew' },
  unknown: { variant: 'outline', labelKey: 'dispatch.truthReconcile.verdict.unknown' },
} as const satisfies Record<
  Verdict,
  { variant: NonNullable<BadgeProps['variant']>; labelKey: string }
>;

// Severity order: the tiles render, and non-match rows sort, in this order.
// Matches sit last (and are collapsed by default). Also the normalization
// target set — a deploy-skew verdict outside this closed set is treated as
// `unknown` (mirrors the badge fall-back) so it still counts, sorts, and
// filters instead of vanishing.
const VERDICT_ORDER = [
  'drift',
  'maybe_skew',
  'unknown',
  'match',
] as const satisfies readonly Verdict[];

function normVerdict(verdict: string): Verdict {
  return (VERDICT_ORDER as readonly string[]).includes(verdict) ? (verdict as Verdict) : 'unknown';
}

// Tile accent (dot + active fill) per verdict. Kept separate from the row
// badge variants so the two surfaces can be tuned independently.
const VERDICT_TILE = {
  drift: { dot: 'bg-destructive', active: 'border-destructive bg-destructive/10 text-destructive' },
  maybe_skew: {
    dot: 'bg-warning',
    active: 'border-warning bg-warning/10 text-warning',
  },
  unknown: {
    dot: 'bg-muted-foreground',
    active: 'border-muted-foreground bg-muted text-foreground',
  },
  match: {
    dot: 'bg-success',
    active: 'border-success bg-success/10 text-foreground',
  },
} as const satisfies Record<Verdict, { dot: string; active: string }>;

// Render an untyped wire value (`ours.value` / `truth.value` are `unknown`).
// Numbers go through `Intl.NumberFormat(locale)` — never a hardcoded locale;
// objects fall back to compact JSON.
function formatValue(value: unknown, locale: string, emptyLabel: string): string {
  if (value === null || value === undefined) return emptyLabel;
  if (typeof value === 'number') return new Intl.NumberFormat(locale).format(value);
  if (typeof value === 'string' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function formatTimestamp(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    timeZone: getMerchantTz(),
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function SideValue(props: { label: string; value: unknown; source: string }) {
  const { t, i18n } = useTranslation();
  const emptyLabel = t('dispatch.truthReconcile.emptyValue');
  return (
    <div className="flex-1 rounded-md border bg-muted/30 px-3 py-2">
      <p className="text-xs font-medium text-muted-foreground">{props.label}</p>
      <p className="text-sm font-semibold tabular-nums">
        {formatValue(props.value, i18n.language, emptyLabel)}
      </p>
      <p className="text-xs text-muted-foreground">{props.source}</p>
    </div>
  );
}

function VerdictTile(props: {
  verdict: Verdict;
  count: number;
  active: boolean;
  onToggle: (verdict: Verdict) => void;
}) {
  const { t } = useTranslation();
  const label = t(VERDICT_BADGE[props.verdict].labelKey);
  const tile = VERDICT_TILE[props.verdict];
  return (
    <button
      type="button"
      data-testid={`truth-reconcile-tile-${props.verdict}`}
      aria-pressed={props.active}
      aria-label={t('dispatch.truthReconcile.tile.aria', { label, count: props.count })}
      onClick={() => props.onToggle(props.verdict)}
      className={`flex flex-1 items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors ${
        props.active ? tile.active : 'border-border bg-muted/20 text-foreground hover:bg-muted/40'
      }`}
    >
      <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${tile.dot}`} />
      <span className="text-lg font-semibold tabular-nums">{props.count}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </button>
  );
}

function ReconcileItemRow({ item }: { item: ReconcileItem }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Deploy-skew guard: frontend and backend deploy independently, so a wire
  // verdict outside the closed set must not throw during render — fall back
  // to the `unknown` styling rather than crashing the whole card.
  const badge = VERDICT_BADGE[item.verdict] ?? VERDICT_BADGE.unknown;
  // `shopifyRaw` is `unknown` and the backend legitimately emits `null` for
  // "no Shopify counterpart" — treat that like `undefined` (loose `!= null`)
  // so we don't render a meaningless `JSON.stringify(null)` = "null" block.
  const hasEvidence =
    item.evidence !== undefined &&
    ((item.evidence.d1Rows?.length ?? 0) > 0 || item.evidence.shopifyRaw != null);

  return (
    <div
      data-testid={`truth-reconcile-item-${item.key}`}
      className="space-y-2 border-b border-border/60 py-3 last:border-b-0"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{item.label}</span>
        <Badge variant={badge.variant}>{t(badge.labelKey)}</Badge>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <SideValue
          label={t('dispatch.truthReconcile.oursLabel')}
          value={item.ours.value}
          source={item.ours.source}
        />
        <span aria-hidden className="self-center text-muted-foreground">
          ⟷
        </span>
        <SideValue
          label={t('dispatch.truthReconcile.truthLabel')}
          value={item.truth.value}
          source={item.truth.source}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        <span className="font-medium">{t('dispatch.truthReconcile.method')}:</span>{' '}
        <span>{item.method}</span>
      </p>
      {item.action ? (
        <p className="text-xs text-warning">
          <span className="font-medium">{t('dispatch.truthReconcile.action')}:</span>{' '}
          <span>{item.action}</span>
        </p>
      ) : null}
      {hasEvidence ? (
        <div>
          <button
            type="button"
            data-testid={`truth-reconcile-evidence-toggle-${item.key}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((prev) => !prev)}
            className="text-xs text-primary underline-offset-2 hover:underline"
          >
            {expanded
              ? t('dispatch.truthReconcile.evidence.hide')
              : t('dispatch.truthReconcile.evidence.show')}
          </button>
          {expanded ? (
            <div data-testid={`truth-reconcile-evidence-${item.key}`} className="mt-1 space-y-2">
              {item.evidence?.d1Rows && item.evidence.d1Rows.length > 0 ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('dispatch.truthReconcile.evidence.d1Rows')}
                  </p>
                  <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 text-xs">
                    {JSON.stringify(item.evidence.d1Rows, null, 2)}
                  </pre>
                </div>
              ) : null}
              {item.evidence?.shopifyRaw != null ? (
                <div>
                  <p className="text-xs font-medium text-muted-foreground">
                    {t('dispatch.truthReconcile.evidence.shopifyRaw')}
                  </p>
                  <pre className="overflow-x-auto rounded-md bg-muted px-3 py-2 text-xs">
                    {JSON.stringify(item.evidence.shopifyRaw, null, 2)}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ReconcileReportView({ report }: { report: TruthReconcileReport }) {
  const { t, i18n } = useTranslation();
  // `null` = the default view: every non-match row, severity-sorted. Clicking a
  // tile filters to that one verdict (the match tile reveals the collapsed
  // matches); clicking the active tile again clears back to the default.
  const [filter, setFilter] = useState<Verdict | null>(null);
  const toggleFilter = (verdict: Verdict) =>
    setFilter((prev) => (prev === verdict ? null : verdict));

  // Counts are derived from the items (not `report.summary`) so the match tile
  // has a number and a deploy-skew verdict still lands in the `unknown` bucket.
  const counts: Record<Verdict, number> = { drift: 0, maybe_skew: 0, unknown: 0, match: 0 };
  for (const item of report.items) counts[normVerdict(item.verdict)] += 1;

  const rank = (verdict: string) => VERDICT_ORDER.indexOf(normVerdict(verdict));
  const sorted = [...report.items].sort((a, b) => rank(a.verdict) - rank(b.verdict));
  const visible =
    filter === null
      ? sorted.filter((i) => normVerdict(i.verdict) !== 'match')
      : sorted.filter((i) => normVerdict(i.verdict) === filter);

  return (
    <>
      {/* The tiles ARE the summary — no wrapper frame around them (#1676).
          Nesting a bordered box inside a check row inside the checklist card
          made three frames deep for one row of numbers. */}
      <div data-testid="truth-reconcile-summary" className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {VERDICT_ORDER.map((verdict) => (
            <VerdictTile
              key={verdict}
              verdict={verdict}
              count={counts[verdict]}
              active={filter === verdict}
              onToggle={toggleFilter}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t('dispatch.truthReconcile.asOf', {
            shopifyAsOf: formatTimestamp(report.shopifyAsOf, i18n.language),
            d1AsOf: formatTimestamp(report.d1AsOf, i18n.language),
          })}
        </p>
        {!report.coverageComplete ? (
          <p
            data-testid="truth-reconcile-coverage-note"
            className="text-xs font-medium text-warning"
          >
            {t('dispatch.truthReconcile.coverageIncomplete')}
          </p>
        ) : null}
      </div>
      {(report.parseFailures?.length ?? 0) > 0 ? (
        <section
          data-testid="truth-reconcile-parse-failures"
          className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-3 text-destructive"
        >
          <h4 className="text-sm font-semibold">
            {t('dispatch.salesBridge.parseFailuresTitle', {
              count: report.parseFailures?.length ?? 0,
            })}
          </h4>
          <ul className="space-y-1">
            {report.parseFailures?.map((failure) => (
              <li
                key={failure.order_id}
                data-testid={`truth-reconcile-parse-failure-${failure.order_name}`}
                className="text-sm"
              >
                <span className="font-medium">{failure.order_name}</span>
                {' · '}
                {t(parseFailureKindLabel(failure.error_kind))}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {report.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('dispatch.truthReconcile.empty')}</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {filter === null
            ? t('dispatch.truthReconcile.allClear')
            : t('dispatch.truthReconcile.noneInView')}
        </p>
      ) : (
        <div>
          {visible.map((item) => (
            <ReconcileItemRow key={item.key} item={item} />
          ))}
        </div>
      )}
    </>
  );
}

interface TruthReconcileCardProps {
  // The parent owns the query; these mirror its view state so the single
  // unified reconcile action can drive both this Shopify-side block and the
  // Onfleet-side block from one click.
  triggered: boolean;
  report: TruthReconcileReport | undefined;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
}

export function TruthReconcileCard({
  triggered,
  report,
  isFetching,
  isError,
  error,
}: TruthReconcileCardProps) {
  const { t } = useTranslation();
  const errorMessage = error instanceof Error ? error.message : t('error.unknown');
  const driftCount = report?.summary.drift ?? 0;

  return (
    <CollapsibleCheck
      testId="dispatch-truth-reconcile-card"
      variant="row"
      title={t('dispatch.delivered.reconcile.shopifySide')}
      description={t('dispatch.delivered.reconcile.shopifyWhy')}
      // A drifting or failed check must not stay folded away; an unrun one
      // keeps its row but costs no space until the operator runs it.
      defaultOpen={triggered && (isError || driftCount > 0)}
      badge={
        <ReconcileBadge
          triggered={triggered}
          isFetching={isFetching}
          isError={isError}
          alertCount={driftCount}
          alertLabel={t('dispatch.truthReconcile.verdict.drift')}
          okLabel={t('dispatch.delivered.reconcile.match')}
        />
      }
    >
      {!triggered ? (
        <p className="text-sm text-muted-foreground">{t('dispatch.truthReconcile.idle')}</p>
      ) : isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t('dispatch.truthReconcile.error', { message: errorMessage })}
        </p>
      ) : isFetching && report === undefined ? (
        <p className="text-sm text-muted-foreground">{t('dispatch.truthReconcile.running')}</p>
      ) : report ? (
        <ReconcileReportView report={report} />
      ) : null}
    </CollapsibleCheck>
  );
}
